import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarBlank } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { EmptyState, PageHeader } from "@/components/common";
import DateTimeLocalPicker from "@/components/DateTimeLocalPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

function TypeBadge({ private: isPrivate }) {
  if (isPrivate) {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-amber-50 text-amber-800 border-amber-200">
        My Call Back
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border bg-muted text-muted-foreground border-border">
      Campaign
    </span>
  );
}

function StatusCell({ cb }) {
  if (cb.missed) {
    return <span className="text-red-700 font-medium text-sm">Missed</span>;
  }
  const st = (cb.status || "scheduled").toLowerCase();
  if (st === "called" || st === "completed") {
    return <span className="text-emerald-700 text-sm capitalize">{st}</span>;
  }
  return <span className="text-sm capitalize">{st}</span>;
}

function isOpenCallback(cb) {
  return (cb.status || "scheduled").toLowerCase() === "scheduled";
}

export default function CustomerCallbacks() {
  const [items, setItems] = useState([]);
  const [todayCount, setTodayCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [applied, setApplied] = useState({ q: "", date_from: "", date_to: "" });
  const [rescheduleCb, setRescheduleCb] = useState(null);
  const [rescheduleAt, setRescheduleAt] = useState("");
  const [deleteCb, setDeleteCb] = useState(null);

  const applyList = (data) => {
    setItems(data.items || []);
    setTodayCount(data.today_count ?? data.due_count ?? 0);
  };

  const load = useCallback(async (filters = applied) => {
    setError("");
    setLoading(true);
    try {
      const params = {};
      if (filters.q) params.q = filters.q;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const { data } = await api.get("/customer-callbacks", { params });
      applyList(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, e));
    } finally {
      setLoading(false);
    }
  }, [applied]);

  useEffect(() => {
    load(applied);
  }, [load, applied]);

  const applyFilters = (e) => {
    e?.preventDefault?.();
    setApplied({
      q: q.trim(),
      date_from: dateFrom,
      date_to: dateTo,
    });
  };

  const clearFilters = () => {
    setQ("");
    setDateFrom("");
    setDateTo("");
    setApplied({ q: "", date_from: "", date_to: "" });
  };

  const saveReschedule = async () => {
    if (!rescheduleCb?.id || !rescheduleAt) {
      setError("Select a new callback date and time.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const { data } = await api.patch(`/customer-callbacks/${rescheduleCb.id}`, {
        callback_at: rescheduleAt,
      });
      applyList(data.callbacks || data);
      setRescheduleCb(null);
      setRescheduleAt("");
      toast.success("Callback rescheduled");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteCb?.id) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.delete(`/customer-callbacks/${deleteCb.id}`);
      applyList(data.callbacks || data);
      setDeleteCb(null);
      toast.success("Callback deleted");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail, e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="customer-callbacks-page">
      <PageHeader
        title="Call backs"
        subtitle="All callbacks from agents — My Call Back and campaign. Reschedule or delete as needed."
      >
        <Button
          type="button"
          variant="outline"
          className="rounded-sm"
          onClick={() => load(applied)}
          data-testid="customer-callbacks-refresh"
        >
          Refresh
        </Button>
      </PageHeader>

      {todayCount > 0 ? (
        <p className="text-sm text-amber-700 mb-4" data-testid="customer-callbacks-today-hint">
          {todayCount} callback{todayCount === 1 ? "" : "s"} scheduled for today.
        </p>
      ) : null}

      <form
        className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end"
        onSubmit={applyFilters}
        data-testid="customer-callbacks-filters"
      >
        <div className="space-y-1.5 sm:col-span-2 lg:col-span-1">
          <Label htmlFor="cb-search">Search</Label>
          <Input
            id="cb-search"
            placeholder="Name, phone, agent, campaign…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="customer-callbacks-search"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cb-from">From date</Label>
          <Input
            id="cb-from"
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            data-testid="customer-callbacks-date-from"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="cb-to">To date</Label>
          <Input
            id="cb-to"
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            data-testid="customer-callbacks-date-to"
          />
        </div>
        <div className="flex gap-2">
          <Button type="submit" className="rounded-sm" data-testid="customer-callbacks-filter-apply">
            Apply
          </Button>
          <Button
            type="button"
            variant="outline"
            className="rounded-sm"
            onClick={clearFilters}
            data-testid="customer-callbacks-filter-clear"
          >
            Clear
          </Button>
        </div>
      </form>

      {error ? (
        <p className="text-sm text-destructive mb-4" data-testid="customer-callbacks-error">{error}</p>
      ) : null}

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : items.length === 0 ? (
        <EmptyState
          icon={CalendarBlank}
          title="No callbacks found"
          description="When agents schedule a CALLBACK disposition, they appear here — including private My Call Backs. Try clearing filters."
          testid="customer-callbacks-empty"
        />
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Campaign</TableHead>
                <TableHead>Agent</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Comments</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((cb) => (
                <TableRow
                  key={cb.id}
                  className={
                    cb.missed
                      ? "bg-red-50/80 dark:bg-red-950/20"
                      : cb.due
                        ? "bg-amber-50/80 dark:bg-amber-950/20"
                        : undefined
                  }
                  data-testid={`customer-callback-row-${cb.id}`}
                >
                  <TableCell className="whitespace-nowrap text-sm">
                    {cb.scheduled_display || cb.scheduled_at || "—"}
                  </TableCell>
                  <TableCell className="text-sm">{cb.customer_name || "—"}</TableCell>
                  <TableCell className="font-mono text-sm">{cb.phone || "—"}</TableCell>
                  <TableCell className="text-sm truncate max-w-[10rem]" title={cb.campaign_name || ""}>
                    {cb.campaign_name || "—"}
                  </TableCell>
                  <TableCell className="text-sm truncate max-w-[8rem]" title={cb.agent_name || ""}>
                    {cb.agent_name || "—"}
                  </TableCell>
                  <TableCell>
                    <TypeBadge private={cb.private} />
                  </TableCell>
                  <TableCell className="text-sm truncate max-w-[12rem]" title={cb.comments || cb.notes || ""}>
                    {cb.comments || cb.notes || "—"}
                  </TableCell>
                  <TableCell>
                    <StatusCell cb={cb} />
                  </TableCell>
                  <TableCell className="text-right">
                    {isOpenCallback(cb) ? (
                      <div className="inline-flex gap-1.5 justify-end">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 rounded-sm text-xs"
                          disabled={busy}
                          onClick={() => {
                            setRescheduleCb(cb);
                            setRescheduleAt("");
                            setError("");
                          }}
                          data-testid={`customer-callback-reschedule-${cb.id}`}
                        >
                          Reschedule
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 rounded-sm text-xs text-destructive border-destructive/40"
                          disabled={busy}
                          onClick={() => {
                            setDeleteCb(cb);
                            setError("");
                          }}
                          data-testid={`customer-callback-delete-${cb.id}`}
                        >
                          Delete
                        </Button>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog
        open={Boolean(rescheduleCb)}
        onOpenChange={(open) => {
          if (!open) {
            setRescheduleCb(null);
            setRescheduleAt("");
          }
        }}
      >
        <DialogContent className="sm:max-w-md" data-testid="customer-callback-reschedule-dialog">
          <DialogHeader>
            <DialogTitle>Reschedule callback</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              <span className="text-muted-foreground">Customer: </span>
              {rescheduleCb?.customer_name || rescheduleCb?.phone || "—"}
            </p>
            <p className="text-muted-foreground text-xs">
              Current: {rescheduleCb?.scheduled_display || rescheduleCb?.scheduled_at || "—"}
            </p>
            <div className="space-y-1.5">
              <Label>New date & time (account timezone)</Label>
              <DateTimeLocalPicker
                value={rescheduleAt}
                onChange={setRescheduleAt}
                testid="customer-callback-reschedule-at"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              className="rounded-sm"
              disabled={busy}
              onClick={() => { setRescheduleCb(null); setRescheduleAt(""); }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="rounded-sm"
              disabled={busy || !rescheduleAt}
              onClick={saveReschedule}
              data-testid="customer-callback-reschedule-save"
            >
              {busy ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(deleteCb)}
        onOpenChange={(open) => {
          if (!open) setDeleteCb(null);
        }}
      >
        <DialogContent className="sm:max-w-sm" data-testid="customer-callback-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete callback?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Remove callback for{" "}
            <span className="font-medium text-foreground">
              {deleteCb?.customer_name || deleteCb?.phone || "this contact"}
            </span>
            {" "}({deleteCb?.scheduled_display || "—"})? This cannot be undone.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              className="rounded-sm"
              disabled={busy}
              onClick={() => setDeleteCb(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              className="rounded-sm"
              disabled={busy}
              onClick={confirmDelete}
              data-testid="customer-callback-delete-confirm"
            >
              {busy ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
